export type ThemeId =
  | 'classic-green'
  | 'midnight-dark'
  | 'ipl-blue'
  | 'royal-purple'
  | 'sunset-orange'
  | 'crimson-red'

export interface ThemeDefinition {
  id: ThemeId
  name: string
  description: string
  primary: string
  dark: string
  light: string
  accent: string
  bg: string
  cardBg: string
  text: string
  textSecondary: string
  border: string
  inputBorder: string
  isDark?: boolean
}

export const THEMES: Record<ThemeId, ThemeDefinition> = {
  'classic-green': {
    id: 'classic-green',
    name: 'Cricket Green',
    description: 'Classic green grass pitch theme',
    primary: '#2E7D32',
    dark: '#1B5E20',
    light: '#4CAF50',
    accent: '#E8F5E9',
    bg: '#f5f6f8',
    cardBg: '#ffffff',
    text: '#111827',
    textSecondary: '#4b5563',
    border: '#e5e7eb',
    inputBorder: '#d1d5db',
    isDark: false,
  },
  'midnight-dark': {
    id: 'midnight-dark',
    name: 'Midnight Dark',
    description: 'Pitch-black AMOLED dark mode with emerald accents',
    primary: '#10B981',
    dark: '#059669',
    light: '#34D399',
    accent: '#064E3B',
    bg: '#000000',
    cardBg: '#121212',
    text: '#FFFFFF',
    textSecondary: '#A1A1AA',
    border: '#27272A',
    inputBorder: '#3F3F46',
    isDark: true,
  },
  'ipl-blue': {
    id: 'ipl-blue',
    name: 'Champion Blue',
    description: 'Vibrant Mumbai & India electric blue',
    primary: '#1976D2',
    dark: '#0D47A1',
    light: '#42A5F5',
    accent: '#E3F2FD',
    bg: '#f0f4f9',
    cardBg: '#ffffff',
    text: '#0f172a',
    textSecondary: '#475569',
    border: '#cbd5e1',
    inputBorder: '#94a3b8',
    isDark: false,
  },
  'royal-purple': {
    id: 'royal-purple',
    name: 'Royal Purple',
    description: 'Rich Kolkata & Rajasthan imperial purple',
    primary: '#7B1FA2',
    dark: '#4A148C',
    light: '#AB47BC',
    accent: '#F3E5F5',
    bg: '#faf5fc',
    cardBg: '#ffffff',
    text: '#1f1627',
    textSecondary: '#6b5e77',
    border: '#e9d5ff',
    inputBorder: '#d8b4fe',
    isDark: false,
  },
  'sunset-orange': {
    id: 'sunset-orange',
    name: 'Sunrise Orange',
    description: 'Dynamic fiery orange & gold',
    primary: '#E65100',
    dark: '#BF360C',
    light: '#FF9800',
    accent: '#FFF3E0',
    bg: '#fffaf5',
    cardBg: '#ffffff',
    text: '#291809',
    textSecondary: '#785b42',
    border: '#fed7aa',
    inputBorder: '#fdba74',
    isDark: false,
  },
  'crimson-red': {
    id: 'crimson-red',
    name: 'Royal Crimson',
    description: 'Passionate RCB & Punjab deep red',
    primary: '#C62828',
    dark: '#8E0000',
    light: '#EF5350',
    accent: '#FFEBEE',
    bg: '#faf3f3',
    cardBg: '#ffffff',
    text: '#241010',
    textSecondary: '#724949',
    border: '#fecaca',
    inputBorder: '#fca5a5',
    isDark: false,
  },
}

export function applyTheme(themeId: ThemeId) {
  const theme = THEMES[themeId] || THEMES['classic-green']
  const root = document.documentElement
  root.setAttribute('data-theme', theme.id)

  if (theme.isDark) {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }

  root.style.setProperty('--color-cricket-primary', theme.primary)
  root.style.setProperty('--color-cricket-dark', theme.dark)
  root.style.setProperty('--color-cricket-light', theme.light)
  root.style.setProperty('--color-cricket-accent', theme.accent)
  root.style.setProperty('--theme-bg', theme.bg)
  root.style.setProperty('--theme-card-bg', theme.cardBg)
  root.style.setProperty('--theme-text', theme.text)
  root.style.setProperty('--theme-text-secondary', theme.textSecondary)
  root.style.setProperty('--theme-border', theme.border)
  root.style.setProperty('--theme-input-border', theme.inputBorder)

  // Update meta theme-color for browser address bar & mobile status bar
  const metaThemeColor = document.querySelector('meta[name="theme-color"]')
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', theme.isDark ? '#000000' : theme.primary)
  }

  // Save to localStorage
  try {
    localStorage.setItem('cric_theme', theme.id)
  } catch {
    // fallback
  }
}
